import { uuid } from "@gjwtrack/client";
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { boardCardFields, boardProjectFields, type Board, type BoardCardField, type Sprint } from "@gjwtrack/domain";
import type { FieldDef } from "@gjwtrack/query-language";
import { GripVertical, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { client, useClient } from "../lib/client.ts";
import { BUNDLE_KINDS, userName } from "../lib/format.ts";
import { Button, ButtonGroup, Checkbox, Input, InputSize } from "../ui/ring.ts";
import { SimpleSelect } from "../ui/SimpleSelect.tsx";

/**
 * YouTrack's Board Settings: not a dialog but a panel that unfolds between the
 * board's toolbar and its columns, with the board still visible underneath.
 * Every control saves on change (text boxes after a pause), as YouTrack does —
 * there is no Save button and no way to lose an edit by closing the panel.
 *
 * Chart is not implemented; neither are "Link sprints to values", "Ignore
 * subtasks in the board query" and "Allow cards to be assigned to multiple
 * sprints".
 */
export function BoardSettingsPanel({ board, sprints, onClose, onDeleted }: { board: Board; sprints: Sprint[]; onClose: () => void; onDeleted: () => void }) {
  const schema = useClient((s) => s.schema);
  const groups = useClient((s) => s.groups);
  const [tab, setTab] = useState<"general" | "columns" | "card">("general");
  // The panel edits a draft so a control never flickers back while the round trip is in flight;
  // the saved board replaces it, except the text the user may still be typing.
  const [draft, setDraft] = useState<Board>(board);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => setDraft(board), [board.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  const save = async (patch: Record<string, unknown>): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const saved = await client.http.command<Board>("updateBoard", { boardId: board.id, ...patch }, uuid());
      setDraft((d) => ({ ...saved, name: d.name, query: d.query }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  /** Text boxes: keep typing local, save once the typing stops. */
  const saveLater = (patch: Record<string, unknown>): void => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void save(patch), 600);
  };
  const set = (patch: Partial<Board>): void => setDraft((d) => ({ ...d, ...patch }));

  if (!schema) return null;
  const projects = schema.data.projects;
  const openSprints = sprints.filter((s) => !s.archived);
  const owner = draft.createdBy;

  return (
    <div className="shrink-0 border-b border-line bg-surface">
      <div className="flex items-center gap-3 border-b border-line px-6 pt-4">
        <h2 className="text-[15px] font-semibold">
          Board Settings <span className="ml-2 font-normal text-muted">{draft.name}</span>
        </h2>
        <div className="flex-1" />
        {saving && <span className="text-xs text-faint">Saving…</span>}
        <Button icon={X} title="Close settings" onClick={onClose} />
      </div>
      <div className="flex gap-6 border-b border-line px-6 text-sm">
        {(["general", "columns", "card"] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-1 py-2 ${tab === k ? "border-accent font-medium" : "border-transparent text-muted hover:text-ink"}`}
          >
            {k === "general" ? "General" : k === "columns" ? "Columns and Swimlanes" : "Card"}
          </button>
        ))}
      </div>
      <div className="max-w-4xl px-6 py-3">
        {tab === "general" ? (
          <>
            <Row label="Name">
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  value={draft.name}
                  inputClassName="w-64"
                  onChange={(e) => {
                    set({ name: e.target.value });
                    if (e.target.value.trim()) saveLater({ name: e.target.value.trim() });
                  }}
                />
                <span className="text-muted">owned by</span>
                <SimpleSelect
                  value={owner === null ? "" : String(owner)}
                  placeholder="—"
                  options={schema.data.users.map((u) => ({ value: String(u.id), label: u.name || u.login }))}
                  onChange={(v) => v && (set({ createdBy: Number(v) }), void save({ ownerId: Number(v) }))}
                />
              </div>
            </Row>
            <Row label="Projects">
              <div className="flex flex-wrap items-center gap-1.5">
                {draft.projectIds.map((id) => (
                  <span key={id} className="inline-flex items-center gap-1.5 rounded border border-line bg-sunken px-2 py-1 text-[13px]">
                    {schema.projectsById.get(id)?.name ?? `#${id}`}
                    <button
                      type="button"
                      title="Remove project"
                      className="text-faint hover:text-danger"
                      onClick={() => {
                        const next = draft.projectIds.filter((p) => p !== id);
                        if (next.length === 0) return setError("a board needs at least one project");
                        set({ projectIds: next });
                        void save({ projectIds: next });
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <SimpleSelect
                  value=""
                  placeholder="Add project"
                  options={projects.filter((p) => !draft.projectIds.includes(p.id)).map((p) => ({ value: String(p.id), label: `${p.name} (${p.key})` }))}
                  onChange={(v) => {
                    if (!v) return;
                    const next = [...draft.projectIds, Number(v)];
                    set({ projectIds: next });
                    void save({ projectIds: next });
                  }}
                />
              </div>
            </Row>
            <Row label="Can view and use the board" hint="Members can view this board including fields and values that are used in the board settings">
              <SimpleSelect
                full
                value={draft.visibleGroupId === null ? "" : String(draft.visibleGroupId)}
                options={[{ value: "", label: "Everyone who can read the board's projects" }, ...groups.map((g) => ({ value: String(g.id), label: g.name }))]}
                onChange={(v) => {
                  const id = v ? Number(v) : null;
                  set({ visibleGroupId: id });
                  void save({ visibleGroupId: id });
                }}
              />
            </Row>
            <Row label="Can edit board settings" hint="Users with administrator permission can edit the settings of any board that they can view and use">
              <div className="py-1.5">{owner === null ? "Administrators" : `${userName(schema, owner)} and administrators`}</div>
            </Row>
            <Row label="Sprints">
              <ButtonGroup>
                <Button active={draft.sprintsEnabled} onClick={() => !draft.sprintsEnabled && (set({ sprintsEnabled: true }), void save({ sprintsEnabled: true }))}>
                  Enabled
                </Button>
                <Button active={!draft.sprintsEnabled} onClick={() => draft.sprintsEnabled && (set({ sprintsEnabled: false, autoAddSprintId: null }), void save({ sprintsEnabled: false }))}>
                  Disabled
                </Button>
              </ButtonGroup>
            </Row>
            {draft.sprintsEnabled && (
              <Row label="Board behavior">
                <div className="flex flex-col gap-2 py-1">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="board-behavior"
                      checked={draft.autoAddSprintId === null}
                      onChange={() => {
                        set({ autoAddSprintId: null });
                        void save({ autoAddSprintId: null });
                      }}
                    />
                    Manually assign issues
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="board-behavior"
                      disabled={openSprints.length === 0}
                      checked={draft.autoAddSprintId !== null}
                      onChange={() => {
                        const id = openSprints[0]?.id;
                        if (id === undefined) return;
                        set({ autoAddSprintId: id });
                        void save({ autoAddSprintId: id });
                      }}
                    />
                    Automatically add new issues to
                    <SimpleSelect
                      value={draft.autoAddSprintId === null ? "" : String(draft.autoAddSprintId)}
                      placeholder={openSprints.length === 0 ? "no open sprint" : "Select a sprint"}
                      disabled={openSprints.length === 0}
                      options={openSprints.map((s) => ({ value: String(s.id), label: s.name }))}
                      onChange={(v) => {
                        if (!v) return;
                        set({ autoAddSprintId: Number(v) });
                        void save({ autoAddSprintId: Number(v) });
                      }}
                    />
                  </label>
                </div>
              </Row>
            )}
            <Row label="Search">
              <Input
                size={InputSize.FULL}
                value={draft.query}
                inputClassName="font-mono"
                placeholder="Enter a search query to filter the cards on the board"
                onChange={(e) => {
                  set({ query: e.target.value });
                  saveLater({ query: e.target.value.trim() });
                }}
              />
            </Row>
            <div className="mt-3 border-t border-line pt-3">
              <Button
                danger
                onClick={() =>
                  confirm(`Delete board "${draft.name}"? Its sprints and card order go with it.`) &&
                  void client.http
                    .command("deleteBoard", { boardId: board.id }, uuid())
                    .then(onDeleted)
                    .catch((e: Error) => setError(e.message))
                }
              >
                Delete board
              </Button>
            </div>
          </>
        ) : tab === "columns" ? (
          <ColumnsTab draft={draft} set={set} save={save} />
        ) : (
          <CardTab draft={draft} set={set} save={save} />
        )}
        {error && <div className="mt-3 rounded bg-danger-soft px-2 py-1 text-danger">{error}</div>}
      </div>
    </div>
  );
}

/** YouTrack's settings layout: the label on the left, the control on the right, an explanation underneath it. */
function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[13rem_minmax(0,1fr)] items-start gap-4 border-b border-line/60 py-2 last:border-0">
      <div className="pt-2 text-sm text-muted">{label}</div>
      <div className="min-w-0">
        {children}
        {hint && <div className="mt-1 text-xs text-faint">{hint}</div>}
      </div>
    </div>
  );
}

function ColumnsTab({ draft, set, save }: { draft: Board; set: (p: Partial<Board>) => void; save: (p: Record<string, unknown>) => Promise<void> }) {
  const schema = useClient((s) => s.schema);
  if (!schema) return null;
  const columnFields = schema.data.fields.filter((f) => BUNDLE_KINDS.has(f.kind) && f.bundleId !== undefined);
  const elements = schema.elementsByBundle.get(schema.fieldsById.get(draft.columnFieldId)?.bundleId ?? -1) ?? [];
  const setColumns = (columns: Board["columns"]): void => {
    set({ columns });
    void save({ columns });
  };
  return (
    <>
      <Row label="Columns by">
        <SimpleSelect
          value={String(draft.columnFieldId)}
          options={columnFields.map((f) => ({ value: String(f.id), label: f.name }))}
          onChange={(v) => {
            set({ columnFieldId: Number(v) });
            // Columns come back from the server: a new field means all of its values, in order.
            void save({ columnFieldId: Number(v) });
          }}
        />
      </Row>
      <Row label="Columns and WIP limits" hint="Values that are not ticked stay off the board; cards in them are not shown.">
        <div className="flex flex-col gap-1 py-1">
          {elements.map((el) => {
            const col = draft.columns.find((c) => c.elementId === el.id);
            return (
              <div key={el.id} className="flex items-center gap-3">
                <Checkbox
                  label={el.name}
                  containerClassName="w-48"
                  checked={!!col}
                  onChange={() =>
                    setColumns(
                      col
                        ? draft.columns.filter((c) => c.elementId !== el.id)
                        : [...draft.columns, { elementId: el.id, wip: null }].sort((a, b) => elements.findIndex((e) => e.id === a.elementId) - elements.findIndex((e) => e.id === b.elementId)),
                    )
                  }
                />
                <Input
                  type="number"
                  min={0}
                  placeholder="WIP"
                  disabled={!col}
                  value={col?.wip ?? ""}
                  inputClassName="w-20"
                  onChange={(e) => setColumns(draft.columns.map((c) => (c.elementId === el.id ? { ...c, wip: e.target.value === "" ? null : Number(e.target.value) } : c)))}
                />
              </div>
            );
          })}
        </div>
      </Row>
      <Row label="Swimlanes by">
        <SimpleSelect
          value={draft.swimlaneFieldId === null ? "" : String(draft.swimlaneFieldId)}
          placeholder="None"
          options={[
            { value: "", label: "None" },
            ...columnFields.filter((f) => f.id !== draft.columnFieldId).map((f) => ({ value: String(f.id), label: f.name })),
            ...schema.data.fields.filter((f) => f.kind === "user").map((f) => ({ value: String(f.id), label: f.name })),
          ]}
          onChange={(v) => {
            const id = v ? Number(v) : null;
            set({ swimlaneFieldId: id });
            void save({ swimlaneFieldId: id });
          }}
        />
      </Row>
    </>
  );
}

/**
 * YouTrack's Board Settings → Card: what a card shows. The fields table is the
 * board's `cardFields`; a board that has never been configured stores null and
 * the table shows the default (every option-set and user field of its
 * projects), so the first edit writes that list out in full and "Restore
 * default" puts it back to null.
 *
 * "Allow cards to be assigned to multiple sprints" is not implemented and is
 * left out rather than shown dead.
 */
function CardTab({ draft, set, save }: { draft: Board; set: (p: Partial<Board>) => void; save: (p: Record<string, unknown>) => Promise<void> }) {
  const schema = useClient((s) => s.schema);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  if (!schema) return null;

  // Everything here is about the board's own projects. A field that is still selected but has left the projects stays
  // in its dropdown, so a stale setting is visible instead of silently blank.
  const onBoard = boardProjectFields(draft, schema);
  const withCurrent = (fields: FieldDef[], current: number | null): FieldDef[] => {
    if (current === null || fields.some((f) => f.id === current)) return fields;
    const f = schema.fieldsById.get(current);
    return f ? [...fields, f] : fields;
  };
  const numeric = onBoard.filter((f) => f.kind === "period" || f.kind === "integer" || f.kind === "float");
  const colorFields = onBoard.filter((f) => f.kind === "enum" || f.kind === "state");
  const rows = boardCardFields(draft, schema);
  const inTable = new Set(rows.map((r) => r.fieldId));
  const addable = onBoard.filter((f) => !inTable.has(f.id) && f.id !== draft.columnFieldId);

  const saveFields = (next: BoardCardField[]): void => {
    set({ cardFields: next });
    void save({ cardFields: next });
  };
  const fieldSelect = (label: string, value: number | null, key: "estimationFieldId" | "originalEstimationFieldId" | "colorFieldId", options: FieldDef[], none: string, hint?: string): ReactNode => (
    <Row label={label} {...(hint ? { hint } : {})}>
      <SimpleSelect
        value={value === null ? "" : String(value)}
        placeholder={none}
        options={[{ value: "", label: none }, ...withCurrent(options, value).map((f) => ({ value: String(f.id), label: f.name }))]}
        onChange={(v) => {
          const id = v ? Number(v) : null;
          set({ [key]: id });
          void save({ [key]: id });
        }}
      />
    </Row>
  );
  /** Which of the board's projects actually carry this field — YouTrack lists them so a half-shared field is obvious. */
  const projectsOf = (fieldId: number): string => {
    const names = draft.projectIds.filter((p) => (schema.projectFieldsByProject.get(p) ?? []).some((pf) => pf.fieldId === fieldId)).map((p) => schema.projectsById.get(p)?.name ?? `#${p}`);
    if (names.length === 0) return "—";
    if (names.length <= 2) return names.join(", ");
    return `${names[0]}, ${names[1]} and ${names.length - 2} more`;
  };
  const onDragEnd = (e: DragEndEvent): void => {
    const from = rows.findIndex((r) => r.fieldId === Number(e.active.id));
    const to = e.over ? rows.findIndex((r) => r.fieldId === Number(e.over!.id)) : -1;
    if (from < 0 || to < 0 || from === to) return;
    saveFields(arrayMove(rows, from, to));
  };

  return (
    <>
      {fieldSelect("Current estimation field", draft.estimationFieldId, "estimationFieldId", numeric, "No estimation field")}
      {fieldSelect("Original estimation field", draft.originalEstimationFieldId, "originalEstimationFieldId", numeric, "No original estimation field", "Set, a column header sums both: current / original.")}
      {fieldSelect("Color scheme", draft.colorFieldId, "colorFieldId", colorFields, "None", "Each card gets a stripe in the colour of its value.")}
      <Row label="Custom fields">
        <div className="py-1">
          <div className="grid grid-cols-[1.25rem_minmax(0,1fr)_11rem_8.5rem_1.75rem] items-center gap-2 border-b border-line pb-1 text-xs text-faint">
            <span />
            <span>Name</span>
            <span>Projects</span>
            <span>Display</span>
            <span />
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={rows.map((r) => r.fieldId)} strategy={verticalListSortingStrategy}>
              {rows.map((r) => (
                <CardFieldRow
                  key={r.fieldId}
                  row={r}
                  name={schema.fieldsById.get(r.fieldId)?.name ?? `#${r.fieldId}`}
                  projects={projectsOf(r.fieldId)}
                  onDisplay={(display) => saveFields(rows.map((x) => (x.fieldId === r.fieldId ? { ...x, display } : x)))}
                  onRemove={() => saveFields(rows.filter((x) => x.fieldId !== r.fieldId))}
                />
              ))}
            </SortableContext>
          </DndContext>
          {rows.length === 0 && <div className="py-2 text-faint">No fields on the card; only the id, the summary and the tags are shown.</div>}
          <div className="mt-2 flex items-center gap-2">
            <SimpleSelect
              value=""
              placeholder="+ Add field"
              options={addable.map((f) => ({ value: String(f.id), label: f.name }))}
              disabled={addable.length === 0}
              onChange={(v) => v && saveFields([...rows, { fieldId: Number(v), display: "full" }])}
            />
            <div className="flex-1" />
            <Button
              onClick={() => {
                set({ cardFields: null });
                void save({ cardFields: null });
              }}
            >
              Restore default
            </Button>
          </div>
        </div>
      </Row>
      <Row label="Colors">
        <div className="py-1.5">
          <Checkbox
            label="Show colors for other custom fields"
            checked={draft.cardColors}
            onChange={(e) => {
              set({ cardColors: e.target.checked });
              void save({ cardColors: e.target.checked });
            }}
          />
        </div>
      </Row>
    </>
  );
}

/** One row of the card's field table: drag grip, name, the projects that have it, how much of the value to show, remove. */
function CardFieldRow({ row, name, projects, onDisplay, onRemove }: { row: BoardCardField; name: string; projects: string; onDisplay: (d: BoardCardField["display"]) => void; onRemove: () => void }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: row.fieldId });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`grid grid-cols-[1.25rem_minmax(0,1fr)_11rem_8.5rem_1.75rem] items-center gap-2 border-b border-line/60 py-1 text-[13px] ${isDragging ? "bg-surface shadow" : ""}`}
    >
      <button ref={setActivatorNodeRef} {...attributes} {...listeners} type="button" title="Drag to reorder" tabIndex={-1} className="flex h-6 w-5 cursor-grab items-center justify-center text-faint active:cursor-grabbing">
        <GripVertical size={14} />
      </button>
      <span className="truncate">{name}</span>
      <span className="truncate text-muted">{projects}</span>
      <SimpleSelect
        value={row.display}
        options={[
          { value: "full", label: "Full" },
          { value: "letter", label: "First letter" },
        ]}
        onChange={(v) => onDisplay(v === "letter" ? "letter" : "full")}
      />
      <button type="button" title={`Remove ${name} from the card`} className="flex h-6 w-6 items-center justify-center rounded text-faint hover:text-danger" onClick={onRemove}>
        <Trash2 size={14} />
      </button>
    </div>
  );
}
